/*
 * Tier 1 commission at a glance: how many clients the partner has, whether
 * payout has started, and the rate ladder. Everything comes from
 * PartnerMe.commission_program (the server's numbers); nothing is worked out
 * here beyond which slab the client count falls in. Renders nothing for a
 * partner on a flat rate (no programme).
 */

import type { CommissionProgram, Money } from "../api/client";
import { inr, ordinal, plural, programStatus, rateLabel, slabLabel, toPaise } from "./format";
import "./tier.css";

export default function TierCard({ program, inProgress }: { program: CommissionProgram | null; inProgress?: Money }) {
  if (!program) return null;
  const st = programStatus(program);
  const count = program.client_count;
  const first = program.activation_clients + 1;
  const held = toPaise(inProgress);

  const progress = st.active
    ? st.next && st.slab
      ? ((count - st.slab.from) / (st.next.from - st.slab.from)) * 100
      : 100
    : (count / first) * 100;

  return (
    <section className="card card--pad tier" aria-labelledby="tier-title">
      <div className="card-heading">
        <div>
          <p className="eyebrow">Tier {program.tier} plan</p>
          <h2 id="tier-title">
            {st.active ? (
              <>
                You earn <span className="tier__rate">{rateLabel(st.slab?.rate)}</span> on every client payment
              </>
            ) : (
              <>Your commission starts with your {ordinal(first)} client</>
            )}
          </h2>
          <p className="tier__sub">
            {st.active ? (
              st.next ? (
                <>
                  {plural(count, "client")} so far. {plural(st.toNext, "more client")} to reach{" "}
                  <strong>{rateLabel(st.next.rate)}</strong>.
                </>
              ) : (
                <>{plural(count, "client")} so far. You&apos;re on the top rate.</>
              )
            ) : (
              <>
                <strong>{count}</strong> of {program.activation_clients} clients so far. Until then each client&apos;s
                commission is recorded as <em>In progress</em>
                {held > 0 ? <> ({inr(inProgress)} so far)</> : null}, and it is released when your {ordinal(first)}{" "}
                client joins. {plural(st.toActivate, "more client")} to go.
              </>
            )}
          </p>
        </div>
      </div>

      <div
        className="tier__bar"
        role="progressbar"
        aria-label={st.active ? "Progress to the next rate" : "Progress to payout"}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(Math.min(100, progress))}
      >
        <span style={{ width: `${Math.min(100, progress)}%` }} />
      </div>

      <ol className="tier__slabs" aria-label="Commission rates by number of clients">
        {program.slabs.map((s) => {
          const here = st.active && st.slab === s;
          return (
            <li key={s.from} className={here ? "is-current" : undefined} aria-current={here ? "step" : undefined}>
              <span>{slabLabel(s)}</span>
              <strong className="num">{rateLabel(s.rate)}</strong>
              {here && <small>You are here</small>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
